import { Heart } from "@faire/slate-icons/Heart";
import { tsv } from "@faire/slate/lib/tsv";
import { motion, useReducedMotion } from "framer-motion";
import { memo, useEffect, useRef, useState } from "react";

import DiscoveryProductTile from "../../../components/shared/DiscoveryProductTile";

export type SavedTileProduct = {
  token?: string;
  imageUrl: string;
  brandName: string;
  name: string;
  priceDollars?: number;
  price?: number;
  msrpDollars?: number;
  msrp?: number;
  rating?: number;
  freeShipping?: boolean;
  isBestseller?: boolean;
};

interface FavoriteProductTileProps {
  product: SavedTileProduct;
  isFavorited: boolean;
  /** Play the save animation on the heart (spin → check circle → filled heart). */
  animateSave?: boolean;
  /** Hold the check circle while this product's post-save surface is open. */
  holdSaveAnimation?: boolean;
  /** Fill colour of the saved heart. Defaults to black. */
  savedHeartFill?: SavedHeartFill;
  /** Fill colour of the save animation's check circle. Defaults to the saved
   * heart fill. */
  checkCircleFill?: SavedHeartFill;
  badge?: string;
  freeShipping?: boolean;
  topShop?: boolean;
  minOrder?: string;
  showQuickAdd?: boolean;
  /** Quick-add handlers; when `quickAddQuantity` > 0 the tile shows a stepper. */
  onQuickAdd?: () => void;
  quickAddQuantity?: number;
  onQuickAddIncrement?: () => void;
  onQuickAddDecrement?: () => void;
  onClick?: () => void;
  onFavorite: (
    productToken: string,
    isFavorited: boolean,
    anchorElement: HTMLElement
  ) => void;
}

export const FavoriteProductTile = memo(
  ({
    product,
    isFavorited,
    animateSave,
    holdSaveAnimation,
    savedHeartFill,
    checkCircleFill,
    badge,
    freeShipping,
    topShop,
    minOrder,
    showQuickAdd,
    onQuickAdd,
    quickAddQuantity,
    onQuickAddIncrement,
    onQuickAddDecrement,
    onClick,
    onFavorite,
  }: FavoriteProductTileProps) => {
    const productToken = product.token ?? "";
    const resolvedBadge =
      badge ?? (product.isBestseller ? "Bestseller" : undefined);
    return (
      <div
        className="relative"
        onMouseDownCapture={(event) => {
          const target = event.target;
          if (
            target instanceof Element &&
            target.closest("[data-favorite-button]")
          ) {
            event.stopPropagation();
          }
        }}
      >
        <DiscoveryProductTile
          badge={resolvedBadge}
          brandName={product.brandName}
          freeShipping={freeShipping ?? product.freeShipping}
          hideHeart
          imageUrl={product.imageUrl}
          minOrder={minOrder}
          msrp={product.msrpDollars ?? product.msrp}
          onClick={onClick}
          price={product.priceDollars ?? product.price}
          productName={product.name}
          rating={product.rating}
          showQuickAdd={showQuickAdd}
          onQuickAdd={onQuickAdd}
          quickAddQuantity={quickAddQuantity}
          onQuickAddIncrement={onQuickAddIncrement}
          onQuickAddDecrement={onQuickAddDecrement}
          topShop={topShop}
        />
        <SaveHeartButton
          animateSave={animateSave}
          checkFill={checkCircleFill}
          holdCircle={holdSaveAnimation}
          isFavorited={isFavorited}
          savedFill={savedHeartFill}
          onFavorite={(anchorElement) =>
            onFavorite(productToken, isFavorited, anchorElement)
          }
        />
      </div>
    );
  }
);
FavoriteProductTile.displayName = "FavoriteProductTile";

export type SavedHeartFill = "red" | "black";

interface SaveHeartButtonProps {
  isFavorited: boolean;
  /** Fill colour of the saved heart. Defaults to black. */
  savedFill?: SavedHeartFill;
  /** Fill colour of the save animation's check circle. Defaults to `savedFill`,
   * e.g. a black check that then lands on a red heart. */
  checkFill?: SavedHeartFill;
  /** Play the save animation when an unsaved product is hearted: the heart
   * spins into a red check circle, holds, then spins into the filled heart. */
  animateSave?: boolean;
  /** Keep the check circle up (e.g. while this product's popover / toast is
   * open); the spin back to the filled heart starts when this turns false. */
  holdCircle?: boolean;
  onFavorite: (anchorElement: HTMLElement) => void;
}

// Save animation timeline. Each spin is a full rotation that shrinks to a dot
// at its midpoint, where the icon is swapped (heart → circle, circle → heart).
// The check circle then holds until the post-save surface (popover / toast)
// closes — but at least SAVE_CIRCLE_MIN_HOLD_MS so it always registers.
const SAVE_SPIN_MS = 420;
const SAVE_SPIN_SWAP_MS = SAVE_SPIN_MS / 2;
const SAVE_CIRCLE_MIN_HOLD_MS = 880;
const SAVE_SPIN_KEYFRAMES = {
  rotate: [0, 144, 216, 360],
  scale: [1, 0.15, 0.15, 1],
};
const SAVE_SPIN_TRANSITION = {
  duration: SAVE_SPIN_MS / 1000,
  times: [0, 0.4, 0.6, 1],
  ease: [0.4, 0, 0.2, 1],
} as const;

type SaveAnimationPhase = "idle" | "spin-to-circle" | "circle" | "spin-to-heart";

/** The top-right heart affordance used on product tiles and cart line items. */
export const SaveHeartButton = ({
  isFavorited,
  savedFill = "black",
  checkFill = savedFill,
  animateSave = false,
  holdCircle = false,
  onFavorite,
}: SaveHeartButtonProps) => {
  const shouldReduceMotion = useReducedMotion();
  const [phase, setPhase] = useState<SaveAnimationPhase>("idle");
  const [showCircle, setShowCircle] = useState(false);
  // Minimum check-circle hold elapsed; the spin back also waits for `holdCircle`.
  const [hasHeldCircle, setHasHeldCircle] = useState(false);
  // Bumped per spin so the spinner remounts and replays its keyframes.
  const [spinKey, setSpinKey] = useState(0);
  const timeoutsRef = useRef<number[]>([]);

  useEffect(
    () => () => timeoutsRef.current.forEach((id) => window.clearTimeout(id)),
    []
  );

  const schedule = (callback: () => void, delayMs: number) => {
    timeoutsRef.current.push(window.setTimeout(callback, delayMs));
  };

  // Spin 1: heart → check circle (swap at the shrunken midpoint), then hold.
  const spinToCircle = () => {
    timeoutsRef.current.forEach((id) => window.clearTimeout(id));
    timeoutsRef.current = [];
    setHasHeldCircle(false);
    setSpinKey((currentKey) => currentKey + 1);
    setPhase("spin-to-circle");
    schedule(() => setShowCircle(true), SAVE_SPIN_SWAP_MS);
    schedule(() => setPhase("circle"), SAVE_SPIN_MS);
    schedule(
      () => setHasHeldCircle(true),
      SAVE_SPIN_MS + SAVE_CIRCLE_MIN_HOLD_MS
    );
  };

  // Spin 2: check circle → filled heart. Runs once the circle has held for its
  // minimum and the post-save surface has closed (so both animate out together).
  useEffect(() => {
    if (phase !== "circle" || !hasHeldCircle || holdCircle) {
      return;
    }
    setSpinKey((currentKey) => currentKey + 1);
    setPhase("spin-to-heart");
    schedule(() => setShowCircle(false), SAVE_SPIN_SWAP_MS);
    schedule(() => setPhase("idle"), SAVE_SPIN_MS);
  }, [hasHeldCircle, holdCircle, phase]);

  const isSpinning = phase === "spin-to-circle" || phase === "spin-to-heart";
  // The save commits immediately, but the heart keeps its unsaved look until
  // it has spun into the circle so the sequence reads gray → circle → red.
  const displayFavorited = phase === "spin-to-circle" ? false : isFavorited;

  return (
    <button
      aria-label={
        isFavorited
          ? "Remove product from favorites"
          : "Save product to favorites"
      }
      className={favoriteButtonStyle.cls()}
      data-favorite-button
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        // Ignore taps mid-spin; taps while the check is held still pass through
        // (e.g. a second tap on the heart dismisses the open popover).
        if (isSpinning) {
          return;
        }
        if (
          animateSave &&
          !isFavorited &&
          phase === "idle" &&
          !shouldReduceMotion
        ) {
          spinToCircle();
        }
        onFavorite(event.currentTarget);
      }}
      style={{ fontSize: "24px" }}
      type="button"
    >
      <motion.span
        animate={isSpinning ? SAVE_SPIN_KEYFRAMES : { rotate: 0, scale: 1 }}
        className="inline-flex"
        key={spinKey}
        transition={isSpinning ? SAVE_SPIN_TRANSITION : { duration: 0 }}
      >
        {showCircle ? (
          <SavedCheckCircle fill={checkFill} />
        ) : (
          <Heart
            className={favoriteIconStyle.cls({
              isFavorited: displayFavorited,
              tone:
                displayFavorited && savedFill === "red" ? "favorited" : "default",
            })}
          />
        )}
      </motion.span>
    </button>
  );
};

/** Check circle shown between the two spins of the save animation. Its fill
 * matches the saved-heart fill (red or black). */
const SavedCheckCircle = ({ fill }: { fill: SavedHeartFill }) => (
  <motion.svg
    animate={{ scale: [0, 1.15, 1] }}
    aria-hidden
    className={savedCheckCircleStyle.cls({ fill })}
    fill="none"
    height="1em"
    initial={{ scale: 0 }}
    transition={{ duration: 0.22, ease: [0.34, 1.56, 0.64, 1] }}
    viewBox="0 0 24 24"
    width="1em"
  >
    <circle cx="12" cy="12" r="11" />
    <motion.path
      animate={{ pathLength: 1, opacity: 1 }}
      d="M7.5 12.4l3.1 3.3L16.6 9"
      initial={{ pathLength: 0, opacity: 0 }}
      stroke="var(--slate-color-icon-primary-inverse)"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      transition={{ duration: 0.28, delay: 0.08, ease: "easeOut" }}
    />
  </motion.svg>
);

const favoriteButtonStyle = tsv({
  base: [
    "group absolute top-1 right-1 z-3 m-1 flex items-center justify-center",
    "transition-all duration-50 ease-in enabled:active:scale-[1.2]",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[-webkit-focus-ring-color]",
  ],
});

const savedCheckCircleStyle = tsv({
  base: "[&_circle]:stroke-fs-action-icon-inverse [&_circle]:stroke-1",
  variants: {
    fill: {
      red: "[&_circle]:fill-fs-action-surface-favorited",
      black: "[&_circle]:fill-fs-action-icon-default",
    },
  },
});

const favoriteIconStyle = tsv({
  base: ["[&_path]:stroke-1", "[&_path]:stroke-fs-action-icon-inverse"],
  variants: {
    // Saved-heart fill colour (unsaved hearts stay gray in both treatments).
    tone: {
      default: "[&_path]:fill-fs-action-icon-default",
      favorited: "[&_path]:fill-fs-action-surface-favorited",
    },
    isFavorited: {
      true: [
        "[&_path]:[fill-opacity:1]",
        "[@media(hover:hover)_and_(pointer:fine)]:group-hover:[&_path]:[fill-opacity:1]",
      ],
      false: [
        "[&_path]:[fill-opacity:0.3]",
        "[@media(hover:hover)_and_(pointer:fine)]:group-hover:[&_path]:[fill-opacity:0.8]",
      ],
    },
  },
});
